import { CloseOutlined, DeleteOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons"
import { InputProps, InputRef, Input, Typography } from "antd"
import { TextAreaProps } from "antd/es/input"
import TextArea from "antd/es/input/TextArea"
import { FC, useState, useRef, useEffect, useCallback, CSSProperties } from "react"
import { Translated } from "../../utils/translated"

type TDefaultValueType = string | number | readonly string[] | undefined

const inputSizeForValue = (value: string, input: HTMLInputElement): number => {
  //   let canvas = document.createElement("canvas")
  let canvas = document.getElementById("text-measurement-canvas") as HTMLCanvasElement
  let context = canvas.getContext("2d")
  if (context === null) {
    return 15
  }
  const fontStyle = window.getComputedStyle(input).fontStyle || "normal"
  const fontVariant = window.getComputedStyle(input).fontVariant || "normal"
  const fontWeight = window.getComputedStyle(input).fontWeight || "400"
  const fontSize = window.getComputedStyle(input).fontSize || "14.4px"
  const fontFamily = window.getComputedStyle(input).fontFamily || "Tahoma, sans-serif"
  context.font = `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize} ${fontFamily}`
  const width = context.measureText(value).width + 2 + 2 + 1 // padding 2px on each side + 1px for border-radius
  //   canvas.remove()
  return width
}

export const PDFControlledInput: FC<{
  inputProps: InputProps
  value: TDefaultValueType
  setValue: (value: TDefaultValueType) => void
}> = ({ inputProps, value, setValue }) => {
  const inputRef = useRef<InputRef | null>(null)

  useEffect(() => {
    let _value = value?.toString() || ""
    if (inputRef.current !== null && inputRef.current.input && _value.length > 0) {
      let _width = inputSizeForValue(_value, inputRef.current.input)
      inputRef.current.input.style.width = `${_width}px`
    }
  }, [inputRef.current, value])

  return (
    <Input
      ref={inputRef}
      {...inputProps}
      value={value}
      onChange={(e) => {
        let _value = e.target.value
        setValue(_value)
        if (inputRef.current !== null && inputRef.current.input) {
          let _width = inputSizeForValue(_value, inputRef.current.input)
          inputRef.current.input.style.width = `${_width}px`
        }
      }}
      className="pdf-input"
      variant="borderless"
    />
  )
}

export const PDFInput: FC<InputProps> = (props) => {
  let { defaultValue } = props
  const [value, setValue] = useState<TDefaultValueType>("")

  useEffect(() => {
    let _value = defaultValue
    if (!_value) {
      _value = "N/A"
    }
    setValue(_value)
  }, [defaultValue])

  return <PDFControlledInput inputProps={props} value={value} setValue={setValue} />
}

export const PDFInputTextArea: FC<TextAreaProps> = (props) => {
  return <TextArea {...props} className="pdf-input" variant="borderless" />
}

export const PDFControlledHidableSpan: FC<{
  isHidden: boolean
  setIsHidden: (isHidden: boolean) => void
  children: React.ReactNode
}> = ({ isHidden, setIsHidden, children }) => {
  const iconStyle: CSSProperties = { fontSize: "0.8rem", cursor: "pointer", marginLeft: 2 }

  if (isHidden) {
    return (
      <span className="pdf-invisible">
        <i>
          <Translated keyEn="hidden element" />
        </i>
        <SyncOutlined
          className="pdf-invisible"
          style={iconStyle}
          onClick={() => setIsHidden(false)}
        />
      </span>
    )
  } else {
    return (
      <span>
        {children}
        <CloseOutlined
          className="pdf-invisible"
          style={iconStyle}
          onClick={() => setIsHidden(true)}
        />
      </span>
    )
  }
}

export const PDFHidableSpan: FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isHidden, setIsHidden] = useState(false)

  return (
    <PDFControlledHidableSpan isHidden={isHidden} setIsHidden={setIsHidden} children={children} />
  )
}

export const PDFHidableDiv: FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isHidden, setIsHidden] = useState(false)

  const iconStyle: CSSProperties = {
    position: "absolute",
    cursor: "pointer",
    fontSize: "0.8rem",
    top: "-0.4rem",
    right: "-0.4rem",
  }

  if (isHidden) {
    return (
      <div
        className="pdf-invisible"
        style={{
          position: "relative",
        }}
      >
        <i>
          <Translated keyEn="hidden element" />
        </i>
        <SyncOutlined
          className="pdf-invisible"
          style={iconStyle}
          onClick={() => setIsHidden(false)}
        />
      </div>
    )
  } else {
    return (
      <div
        style={{
          position: "relative",
        }}
      >
        {children}
        <CloseOutlined
          className="pdf-invisible"
          style={iconStyle}
          onClick={() => setIsHidden(true)}
        />
      </div>
    )
  }
}
